import { useTheme } from "@emotion/react";
import {
  Box,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { GenericRechartsTooltip } from "../../components/CustomMUI";
import EventIcon from "@mui/icons-material/Event";

const BreakdownChart = ({ title, data, dataKey, labelKey }) => {
  const theme = useTheme();
  const isEmptyChart = data.length === 0;

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          mb: 1,
          display: "block",
          textAlign: "center",
        }}
      >
        {title}
      </Typography>

      <Box
        sx={{
          width: "100%",
          height: data.length ? data.length * 28 : 50,
          position: "relative",
        }}
      >
        <ResponsiveContainer>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 0, right: 30, bottom: 0, left: 10 }}
          >
            <YAxis
              type="category"
              dataKey={labelKey}
              tick={{ fontSize: 12 }}
              width={90}
              hide={data.length === 0}
            />

            <XAxis type="number" hide />

            <Tooltip
              cursor={{ fill: theme.palette.action.hover }}
              content={
                <GenericRechartsTooltip
                  renderContent={(row) => {
                    // Describe the row by the fields it carries rather than by the
                    // metric toggle's current value: Recharts keeps the hovered row
                    // in its own state, so after a toggle the row can still be from
                    // the previous dataset. Only highest-rated rows have an
                    // average_rating; most-watched rows carry just a watch count.
                    if (row.average_rating == null) {
                      const noun = row.watch_count === 1 ? "movie" : "movies";
                      return `${row.watch_count} ${noun}`;
                    }
                    return `Average rating ${row.average_rating.toFixed(
                      1
                    )} (${row.rating_count} ratings)`;
                  }}
                />
              }
            />

            <Bar
              dataKey={dataKey}
              fill={theme.palette.primary.main}
              radius={[
                0,
                theme.shape.borderRadius,
                theme.shape.borderRadius,
                0,
              ]}
            >
              <LabelList
                dataKey={dataKey}
                position="right"
                formatter={(value) =>
                  dataKey === "average_rating" ? value.toFixed(1) : value
                }
                style={{
                  fill: theme.palette.text.primary,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        {isEmptyChart && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: 12,
                color: "text.secondary",
                textAlign: "center",
                fontStyle: "italic",
              }}
            >
              Not enough data yet
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default function GenreDecadeLanguage({ data, isOwnProfile, subject }) {
  const [breakdownMetric, setBreakdownMetric] = useState("most_watched");
  const languageNames = new Intl.DisplayNames(["en"], { type: "language" });
  const withLanguageLabels = (data) =>
    data.map((row) => ({
      ...row,
      language_label: languageNames.of(row.language) ?? row.language,
    }));

  return (
    <Grid
      item
      xs={12}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        width: "100%",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mt: 1,
        }}
      >
        <Stack direction="column" spacing={1}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <EventIcon color="primary" />
            <Typography variant="h5" sx={{ fontWeight: "bold", lineHeight: 1 }}>
              {isOwnProfile ? "Your watches" : "Watches"} by category...
            </Typography>
          </Stack>
          <Typography variant="caption">
            {isOwnProfile ? "Your" : <em>{subject}’s</em>} top 10 genres,
            decades and languages — what {isOwnProfile ? "you" : "they"} watched
            most, and what {isOwnProfile ? "you" : "they"} rated highest (min. 3
            ratings)
          </Typography>
        </Stack>

        <ToggleButtonGroup
          size="small"
          value={breakdownMetric}
          exclusive
          onChange={(_, value) => value && setBreakdownMetric(value)}
          sx={{ alignSelf: "center" }}
        >
          <ToggleButton value="most_watched">Most watched</ToggleButton>
          <ToggleButton value="highest_rated">Highest rated</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={4}>
          <BreakdownChart
            title="By genre"
            data={data.genre[breakdownMetric]}
            dataKey={
              breakdownMetric === "most_watched"
                ? "watch_count"
                : "average_rating"
            }
            labelKey="genre"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <BreakdownChart
            title="By decade"
            data={data.decade[breakdownMetric]}
            dataKey={
              breakdownMetric === "most_watched"
                ? "watch_count"
                : "average_rating"
            }
            labelKey="decade"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <BreakdownChart
            title="By language"
            data={withLanguageLabels(data.language[breakdownMetric])}
            dataKey={
              breakdownMetric === "most_watched"
                ? "watch_count"
                : "average_rating"
            }
            labelKey="language_label"
          />
        </Grid>
      </Grid>
    </Grid>
  );
}
